/**
 * This tab's id, so it can tell its own writes from somebody else's.
 *
 * Every mutation on the server announces itself over SSE (see `collectionEvents.ts`), and the frame
 * carries whoever caused it. A tab ignores the frames it caused, because the surface that made the
 * write already reloads locally and holds optimistic ±1 state — without that, a save would round
 * trip into a refetch that fights the update already on screen.
 *
 * **What the header means is narrower than "I sent this request".** It means *"this request's
 * effect is already reflected in my local state"*. So it rides on the direct-edit helpers — the
 * dock's saves (`note.ts`), an add from Read (`search.ts`), deck and tag admin (`setup.ts`) — and
 * deliberately **not** on `jobs.ts`. Pressing Run on a frequency sort is not accounting for it: the
 * job rewrites thousands of notes and this tab has no local picture of that, so it must refetch
 * like everybody else. The server enforces the same rule from its side rather than trusting this
 * one, since a client that remembered wrongly would fail silently.
 *
 * **Nor on `cards.ts`, which is the near miss worth naming.** Those requests plainly come from this
 * tab, and Cards does reload its own rows after each one — but the state the frame exists to
 * refresh is the deck counters `App` holds, and a delete or a reposition moves those with nothing
 * on this side to account for them. `App` never unmounts, so a wrongly-claimed id there is not one
 * stale render but a number that stays wrong for the session.
 *
 * **A module constant, not `sessionStorage`.** The redirect guard in `session.ts` persists because
 * it has to survive the trip through Google; an id that outlived a reload would buy nothing here.
 * A reloaded tab has no optimistic state left to protect and should refetch like a stranger.
 *
 * Kept to the charset the server accepts (`[A-Za-z0-9_-]{1,64}`) — anything else is read as absent,
 * which costs one harmless extra refetch rather than failing a write.
 */
export const CLIENT_ID: string = makeId();

export const CLIENT_ID_HEADER = "X-Client-Id";

/**
 * Did THIS tab cause the write that produced this frame?
 *
 * A one-line comparison, extracted because the interesting case is the one it does not look like it
 * is about. **`null` is not "unknown", it is "definitely somebody else"** — the MCP, a Cowork bulk
 * pass, the 03:00 scheduler — and it is the case the whole feature was built for. Writing the guard
 * as `writer && writer !== CLIENT_ID` reads perfectly naturally and would silently switch every one
 * of those back off, leaving exactly the staleness 日本-283 exists to fix, with nothing red
 * anywhere. So the rule is pinned somewhere a test can reach it rather than living inline in a
 * `useEffect`.
 */
export function causedHere(writer: string | null): boolean {
  return writer !== null && writer === CLIENT_ID;
}

function makeId(): string {
  // `randomUUID` needs a secure context. Production is HTTPS and dev is localhost, so both qualify
  // — but a fallback costs one line and beats a crash on the boot path of the whole app.
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;
  return `tab-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}
